import { useEffect, useState, type FormEvent } from 'react';
import { AddressEntry } from './AddressEntry';
import { addressSummary } from './address';
import {
  api,
  ApiError,
  routePath,
  type DistributorWithRoutes,
  type RepRequest,
  type Role,
  type SupportRequest,
  type AddressReviewResponse,
} from './api';
import {
  approvalBlocker,
  canFlag,
  canReject,
  emptyForm,
  formatDateTime,
  fullAddressOf,
  isOpenStatus,
  phonesOf,
  REP_STATUS_LABEL,
  SUPPORT_STATUS_LABEL,
  toggleExpanded,
  toNewStopInput,
  TYPE_LABEL,
  validateNewStop,
  type FormErrors,
  type NewStopFormValues,
} from './requests';

type Loadable<T> =
  { state: 'loading' } | { state: 'ready'; data: T } | { state: 'error'; message: string };

/** Optional advice; neither the result nor a provider failure gates a review decision. */
function ReviewAddressCheck({
  request,
  userId,
}: {
  request: SupportRequest;
  userId: number | null;
}) {
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState<AddressReviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function validate() {
    if (busy) return;
    setBusy(true);
    setError(null);
    setReview(null);
    try {
      const response = await api.validateRequestAddress(userId, request.id);
      setReview(response);
    } catch (failure) {
      setError(describeError(failure, 'Address review is unavailable.'));
    } finally {
      setBusy(false);
    }
  }
  const result = review?.result;
  const suggested = result?.action === 'accept' ? result.address : result?.suggestedAddress;
  return (
    <section className="review-address-check" aria-label="Address review">
      <div className="address-entry-heading list-actions">
        <h4>Address review</h4>
        <button type="button" onClick={validate} disabled={busy}>
          {busy ? 'Checking address…' : 'Validate & find similar'}
        </button>
      </div>
      {result && (
        <div
          className={`address-validation-review address-validation-${result.action}`}
          role="status"
        >
          <strong>
            {result.action === 'accept'
              ? 'Google confirmed the address'
              : result.action === 'confirm'
                ? 'Google suggests a standardized address'
                : result.action === 'add_unit'
                  ? 'Suite / unit may need review'
                  : 'Google could not confirm the address'}
          </strong>
          {result.action !== 'accept' && <p>{result.message}</p>}
          {suggested && (
            <div className="address-comparison">
              <div>
                <span>Submitted address</span>
                <p>{addressSummary(request.customer)}</p>
              </div>
              <div>
                <span>Google address</span>
                <p>{addressSummary(suggested)}</p>
              </div>
            </div>
          )}
          <div className="google-validation-attribution">
            <img src="/google-maps-attribution.svg" alt="Google Maps" height="18" />
          </div>
        </div>
      )}
      {review?.validationError && (
        <p className="form-error" role="status">
          {review.validationError}
        </p>
      )}
      {review?.similarAddresses && (
        <section className="similar-addresses" aria-label="Similar registry addresses">
          <h5>Similar addresses</h5>
          <p role="status">
            {review.similarAddresses.total === 0
              ? 'No similar addresses found in the registry.'
              : `Showing ${review.similarAddresses.matches.length} of ${review.similarAddresses.total} potential matches.`}
          </p>
          {review.similarAddresses.matches.length > 0 && (
            <ul className="similar-address-list">
              {review.similarAddresses.matches.map((match) => (
                <li key={match.stopId}>
                  <div>
                    <strong>{match.customerName}</strong>
                    <p>
                      {match.streetAddress}
                      <br />
                      {[match.city, match.state, match.zipCode].filter(Boolean).join(' ')}
                    </p>
                  </div>
                  <div className="similar-address-route">
                    <span>{match.distributorName}</span>
                    <a
                      href={routePath(match.distributorId, match.routeNumber)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View route {match.routeNumber}
                      <span className="visually-hidden"> (opens in a new tab)</span>
                    </a>
                  </div>
                  <div className="similar-address-reasons">
                    {match.reasons.map((reason) => (
                      <span key={reason}>{reason}</span>
                    ))}
                    {match.comparedWith === 'google' && (
                      <span>Compared with Google’s suggested address</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {review?.similarAddressesError && (
        <p className="form-error" role="alert">
          {review.similarAddressesError}
        </p>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

const describeError = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * The Requests tab. Distributor reps request a new stop and follow their own
 * requests; Xtreme support works the queue. Which one appears is decided by
 * the account's role here only for layout: the API enforces it again.
 */
export function RequestsPage({
  distributors,
  userId,
  role,
}: {
  distributors: DistributorWithRoutes[];
  userId: number | null;
  role: Role;
}) {
  return (
    <section className="requests-page" aria-label="Requests">
      {role === 'support_admin' ? (
        <SupportQueue userId={userId} />
      ) : (
        <RepRequests distributors={distributors} userId={userId} />
      )}
    </section>
  );
}

/* ---------- distributor rep ---------- */

function RepRequests({
  distributors,
  userId,
}: {
  distributors: DistributorWithRoutes[];
  userId: number | null;
}) {
  const [mine, setMine] = useState<Loadable<RepRequest[]>>({ state: 'loading' });
  const [formOpen, setFormOpen] = useState(false);
  const [notice, setNotice] = useState<{
    kind: 'in_review' | 'submitted';
    request: RepRequest;
  } | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setMine({ state: 'loading' });
    api
      .myRequests(userId)
      .then(({ requests }) => !cancelled && setMine({ state: 'ready', data: requests }))
      .catch(
        (error: unknown) =>
          !cancelled &&
          setMine({
            state: 'error',
            message: describeError(error, 'Your requests could not load.'),
          }),
      );
    return () => {
      cancelled = true;
    };
  }, [userId, reload]);

  function finished(kind: 'in_review' | 'submitted', request: RepRequest) {
    setNotice({ kind, request });
    setFormOpen(false);
    setReload((value) => value + 1);
  }

  return (
    <>
      <div className="requests-heading">
        <div>
          <h2>Your requests</h2>
        </div>
        <div className="list-actions">
          <button
            type="button"
            onClick={() => {
              setNotice(null);
              setFormOpen(true);
            }}
            disabled={formOpen || distributors.length === 0}
            title={
              distributors.length === 0 ? 'No distributor is assigned to this account' : undefined
            }
          >
            Request New Stop
          </button>
        </div>
      </div>

      {notice && (
        <div className={`panel request-notice request-notice-${notice.kind}`} role="status">
          <h3>{notice.kind === 'in_review' ? 'In review' : 'Request submitted'}</h3>
          <p>
            {notice.kind === 'in_review'
              ? `${notice.request.customer.name} is in review.`
              : `Your request for ${notice.request.customer.name} on route ${notice.request.routeNumber} is awaiting Xtreme review.`}
          </p>
        </div>
      )}

      {formOpen && (
        <NewStopForm
          distributors={distributors}
          userId={userId}
          onCancel={() => setFormOpen(false)}
          onConflict={(request) => finished('in_review', request)}
          onSubmitted={(request) => finished('submitted', request)}
        />
      )}

      {mine.state === 'loading' && (
        <div className="panel muted" role="status">
          Loading your requests…
        </div>
      )}
      {mine.state === 'error' && (
        <div className="panel error" role="alert">
          {mine.message}{' '}
          <button type="button" onClick={() => setReload((value) => value + 1)}>
            Try again
          </button>
        </div>
      )}
      {mine.state === 'ready' && mine.data.length === 0 && (
        <div className="panel empty">No requests yet.</div>
      )}
      {mine.state === 'ready' && mine.data.length > 0 && (
        <div className="customers-table-scroll">
          <table className="requests-table">
            <caption className="visually-hidden">Your new stop requests</caption>
            <thead>
              <tr>
                <th scope="col">Customer</th>
                <th scope="col">Distributor · Route</th>
                <th scope="col">Submitted</th>
                <th scope="col">Status</th>
                <th scope="col">Decision</th>
              </tr>
            </thead>
            <tbody>
              {mine.data.map((request) => (
                <tr key={request.id} data-request-id={request.id}>
                  <th scope="row">
                    {request.customer.name}
                    <span className="request-address">{fullAddressOf(request.customer)}</span>
                  </th>
                  <td>
                    {request.distributorName} · Route {request.routeNumber}
                  </td>
                  <td>{formatDateTime(request.submittedAt)}</td>
                  <td>
                    <span className={`status-pill status-${request.status}`}>
                      {REP_STATUS_LABEL[request.status]}
                    </span>
                  </td>
                  <td className="request-decision-cell">
                    {request.status === 'approved' && (
                      <>
                        Added to{' '}
                        <a href={routePath(request.distributorId, request.routeNumber)}>
                          route {request.routeNumber}
                        </a>
                        {request.decidedAt ? ` on ${formatDateTime(request.decidedAt)}` : ''}.
                      </>
                    )}
                    {request.status === 'rejected' && (
                      <>
                        <strong>Feedback from Xtreme:</strong> {request.feedback}
                      </>
                    )}
                    {request.status === 'pending' && 'Waiting for Xtreme.'}
                    {request.status === 'in_review' && 'Xtreme is reviewing this request.'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function NewStopForm({
  distributors,
  userId,
  onCancel,
  onConflict,
  onSubmitted,
}: {
  distributors: DistributorWithRoutes[];
  userId: number | null;
  onCancel: () => void;
  onConflict: (request: RepRequest) => void;
  onSubmitted: (request: RepRequest) => void;
}) {
  const [values, setValues] = useState<NewStopFormValues>(() => emptyForm(distributors));
  const [errors, setErrors] = useState<FormErrors>({});
  const [stage, setStage] = useState<'entry' | 'confirm'>('entry');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const distributor = distributors.find((item) => String(item.id) === values.distributorId);
  const routes = distributor?.routes ?? [];

  function update(key: keyof NewStopFormValues, value: string) {
    setValues((current) => {
      const next = { ...current, [key]: value };
      if (key === 'distributorId') {
        const chosen = distributors.find((item) => String(item.id) === value);
        next.routeId = chosen && chosen.routes.length === 1 ? String(chosen.routes[0]!.id) : '';
      }
      return next;
    });
    // Any edit after a clear check needs a fresh check.
    setStage('entry');
    setErrors({});
    setFailure(null);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const validation = validateNewStop(values);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    const input = toNewStopInput(values);
    setBusy(true);
    setFailure(null);
    try {
      if (stage === 'entry') {
        const result = await api.checkNewStop(userId, input);
        if (result.outcome === 'conflict') onConflict(result.request);
        else setStage('confirm');
      } else {
        const result = await api.submitNewStop(userId, input);
        if (result.outcome === 'conflict') onConflict(result.request);
        else onSubmitted(result.request);
      }
    } catch (error) {
      setFailure(describeError(error, 'The request could not be sent.'));
    } finally {
      setBusy(false);
    }
  }

  const field = (
    key: keyof NewStopFormValues,
    label: string,
    options: { required?: boolean; span?: boolean; maxLength?: number; placeholder?: string } = {},
  ) => (
    <div className={`form-field${options.span ? ' span-2' : ''}`}>
      <label htmlFor={`new-stop-${key}`}>
        {label}
        {options.required ? <span className="required-mark"> *</span> : ''}
      </label>
      <input
        id={`new-stop-${key}`}
        value={values[key]}
        onChange={(event) => update(key, event.target.value)}
        maxLength={options.maxLength ?? 200}
        placeholder={options.placeholder}
        aria-invalid={errors[key] ? true : undefined}
        aria-describedby={errors[key] ? `new-stop-${key}-error` : undefined}
        autoComplete="off"
        disabled={busy}
      />
      {errors[key] && (
        <p className="form-error" id={`new-stop-${key}-error`}>
          {errors[key]}
        </p>
      )}
    </div>
  );

  return (
    <form
      className="panel new-stop-form"
      onSubmit={submit}
      noValidate
      aria-labelledby="new-stop-title"
    >
      <h3 id="new-stop-title">Request New Stop</h3>
      <div className="form-grid">
        <div className="form-field">
          <label htmlFor="new-stop-distributorId">
            Distributor<span className="required-mark"> *</span>
          </label>
          <select
            id="new-stop-distributorId"
            value={values.distributorId}
            onChange={(event) => update('distributorId', event.target.value)}
            disabled={busy || distributors.length === 1}
            aria-invalid={errors.distributorId ? true : undefined}
          >
            <option value="">Choose a distributor</option>
            {distributors.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          {errors.distributorId && <p className="form-error">{errors.distributorId}</p>}
        </div>
        <div className="form-field">
          <label htmlFor="new-stop-routeId">
            Route<span className="required-mark"> *</span>
          </label>
          <select
            id="new-stop-routeId"
            value={values.routeId}
            onChange={(event) => update('routeId', event.target.value)}
            disabled={busy || !distributor}
            aria-invalid={errors.routeId ? true : undefined}
          >
            <option value="">
              {distributor ? 'Choose a route' : 'Choose a distributor first'}
            </option>
            {routes.map((route) => (
              <option key={route.id} value={route.id}>
                Route {route.routeNumber} · {plural(route.stopCount, 'customer')}
              </option>
            ))}
          </select>
          {errors.routeId && <p className="form-error">{errors.routeId}</p>}
        </div>
        {field('name', 'Customer name', { required: true, placeholder: 'Business name' })}
        <AddressEntry
          value={values}
          userId={userId}
          errors={errors}
          disabled={busy}
          onChange={(address) => {
            setValues((current) => ({
              ...current,
              ...address,
              googlePlaceId: address.googlePlaceId,
            }));
            setStage('entry');
            setErrors({});
            setFailure(null);
          }}
        />
        {field('contactName', 'Contact', { maxLength: 100 })}
        {field('primaryPhone', 'Phone', { maxLength: 30 })}
        {field('secondaryPhone', 'Other phone', { maxLength: 30 })}
      </div>
      {stage === 'confirm' && (
        <div className="form-confirm" role="status">
          No matching customer name and street address found.
        </div>
      )}
      {failure && (
        <p className="form-error" role="alert">
          {failure}
        </p>
      )}
      <div className="form-actions list-actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Working…' : stage === 'confirm' ? 'Request a New Stop' : 'Submit for check'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/* ---------- Xtreme support ---------- */

function SupportQueue({ userId }: { userId: number | null }) {
  const [view, setView] = useState<'open' | 'history'>('open');
  const [data, setData] = useState<Loadable<SupportRequest[]>>({ state: 'loading' });
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set());
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData({ state: 'loading' });
    api
      .requestQueue(userId, view)
      .then(({ requests }) => !cancelled && setData({ state: 'ready', data: requests }))
      .catch(
        (error: unknown) =>
          !cancelled &&
          setData({ state: 'error', message: describeError(error, 'Requests could not load.') }),
      );
    return () => {
      cancelled = true;
    };
  }, [userId, view, reload]);

  /** A decided request leaves the open list; anything else is updated in place. */
  function changed(updated: SupportRequest, message: string) {
    setNotice(message);
    setData((current) => {
      if (current.state !== 'ready') return current;
      const leaves = view === 'open' && !isOpenStatus(updated.status);
      return {
        state: 'ready',
        data: leaves
          ? current.data.filter((item) => item.id !== updated.id)
          : current.data.map((item) => (item.id === updated.id ? updated : item)),
      };
    });
  }

  /** The request changed since this list was loaded: say so and load its current state. */
  function stale(message: string) {
    setNotice(message);
    setReload((value) => value + 1);
  }

  return (
    <>
      <div className="requests-heading">
        <div>
          <h2>Requests</h2>
        </div>
        <div className="queue-tabs" role="group" aria-label="Which requests to show">
          <button type="button" aria-pressed={view === 'open'} onClick={() => setView('open')}>
            Open
          </button>
          <button
            type="button"
            aria-pressed={view === 'history'}
            onClick={() => setView('history')}
          >
            History
          </button>
          <button
            type="button"
            onClick={() => setReload((value) => value + 1)}
            title="Reload the list"
          >
            Refresh
          </button>
        </div>
      </div>

      {notice && (
        <div className="panel request-notice" role="status">
          <p>{notice}</p>
        </div>
      )}

      {data.state === 'loading' && (
        <div className="panel muted" role="status">
          Loading requests…
        </div>
      )}
      {data.state === 'error' && (
        <div className="panel error" role="alert">
          {data.message}{' '}
          <button type="button" onClick={() => setReload((value) => value + 1)}>
            Try again
          </button>
        </div>
      )}
      {data.state === 'ready' && (
        <>
          <div className="customers-table-summary">
            <span aria-live="polite">
              {plural(data.data.length, view === 'open' ? 'open request' : 'decided request')}
            </span>
          </div>
          {data.data.length === 0 ? (
            <div className="panel empty">
              {view === 'open'
                ? 'No requests are waiting for review.'
                : 'No requests have been decided yet.'}
            </div>
          ) : (
            <div className="customers-table-scroll">
              <table className="customers-table requests-table">
                <caption className="visually-hidden">
                  {view === 'open' ? 'Open new stop requests' : 'Decided new stop requests'}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Customer</th>
                    <th scope="col">Distributor · Route</th>
                    <th scope="col">Type</th>
                    <th scope="col">Status</th>
                    <th scope="col">Submitted</th>
                  </tr>
                </thead>
                {data.data.map((request) => {
                  const open = expanded.has(request.id);
                  return (
                    <tbody key={request.id} data-request-id={request.id}>
                      <tr className="customers-route-row request-row">
                        <th scope="row">
                          <button
                            type="button"
                            className="customers-route-toggle"
                            aria-expanded={open}
                            aria-controls={`request-details-${request.id}`}
                            onClick={() =>
                              setExpanded((current) => toggleExpanded(current, request.id))
                            }
                          >
                            <span className="customers-chevron" aria-hidden="true">
                              {open ? '▾' : '▸'}
                            </span>
                            <strong>{request.customer.name}</strong>
                          </button>
                        </th>
                        <td>
                          {request.distributorName} · Route {request.routeNumber}
                        </td>
                        <td>{TYPE_LABEL[request.type]}</td>
                        <td>
                          <span className={`status-pill status-${request.status}`}>
                            {SUPPORT_STATUS_LABEL[request.status]}
                          </span>
                        </td>
                        <td>{formatDateTime(request.submittedAt)}</td>
                      </tr>
                      {open && (
                        <tr className="customers-child-row request-details-row">
                          <td colSpan={5} id={`request-details-${request.id}`}>
                            <RequestDetails
                              request={request}
                              userId={userId}
                              onChanged={changed}
                              onStale={stale}
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  );
                })}
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

export function RequestDetails({
  request,
  userId,
  onChanged,
  onStale,
}: {
  request: SupportRequest;
  userId: number | null;
  onChanged: (updated: SupportRequest, message: string) => void;
  /** The server refused the action because the request is no longer in the state shown. */
  onStale: (message: string) => void;
}) {
  const [busy, setBusy] = useState<'flag' | 'approve' | 'reject' | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState<string | null>(null);
  const open = isOpenStatus(request.status);
  const blocker = approvalBlocker(request);
  const { customer, match, differences } = request;

  async function run(action: 'flag' | 'approve' | 'reject') {
    setBusy(action);
    setError(null);
    try {
      if (action === 'flag') {
        const { request: updated } = await api.flagRequest(userId, request.id, request);
        onChanged(
          updated,
          `Request #${updated.id} for ${updated.customer.name} is flagged with Tropicana.`,
        );
      } else if (action === 'approve') {
        const result = await api.approveRequest(userId, request.id, request);
        onChanged(
          result.request,
          result.outcome === 'approved'
            ? `Request #${result.request.id} approved: ${result.request.customer.name} was added to ${result.request.distributorName} route ${result.request.routeNumber}.`
            : `Request #${result.request.id} now matches an existing customer and was returned to conflict review. Nothing was added.`,
        );
      } else {
        const { request: updated } = await api.rejectRequest(
          userId,
          request.id,
          feedback.trim(),
          request,
        );
        onChanged(
          updated,
          `Request #${updated.id} for ${updated.customer.name} was rejected with feedback. It is now under History.`,
        );
      }
    } catch (failure) {
      const message = describeError(failure, 'The action could not be completed.');
      // 409: another reviewer changed or decided this request first. Nothing
      // was saved; reload the list so the row shows what is true now.
      if (failure instanceof ApiError && failure.status === 409) onStale(message);
      else setError(message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="request-details">
      <dl>
        <div>
          <dt>Customer</dt>
          <dd>{customer.name}</dd>
        </div>
        <div>
          <dt>Street address</dt>
          <dd>{customer.streetAddress}</dd>
        </div>
        <div>
          <dt>City, state, ZIP</dt>
          <dd>
            {customer.city} {customer.state} {customer.zipCode}
          </dd>
        </div>
        <div>
          <dt>Contact</dt>
          <dd>{customer.contactName || '—'}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{customer.primaryPhone || '—'}</dd>
        </div>
        <div>
          <dt>Other phone</dt>
          <dd>{customer.secondaryPhone || '—'}</dd>
        </div>
        <div>
          <dt>Requested for</dt>
          <dd>
            {request.distributorName} ·{' '}
            <a href={routePath(request.distributorId, request.routeNumber)}>
              Route {request.routeNumber}
            </a>
          </dd>
        </div>
        <div>
          <dt>Requested by</dt>
          <dd>
            {request.requesterName} · {formatDateTime(request.submittedAt)}
          </dd>
        </div>
      </dl>

      {open && (
        <ReviewAddressCheck
          key={`${request.id}:${customer.streetAddress}:${customer.city}:${customer.state}:${customer.zipCode}`}
          request={request}
          userId={userId}
        />
      )}

      {match && (
        <div className="request-match" aria-label="Matching existing customer">
          <h4>Matching existing customer</h4>
          <p className="match-legend">
            Same name and street address. {differences?.route && <strong>Route differs. </strong>}
            {differences?.phone && <strong>Phone differs. </strong>}
            {differences &&
              !differences.route &&
              !differences.phone &&
              'Route and phone also match.'}
          </p>
          <table className="match-table">
            <thead>
              <tr>
                <th scope="col"></th>
                <th scope="col" className={differences?.route ? 'request-diff' : undefined}>
                  Distributor · Route
                </th>
                <th scope="col">Customer</th>
                <th scope="col">Address</th>
                <th scope="col">Contact</th>
                <th scope="col" className={differences?.phone ? 'request-diff' : undefined}>
                  Phone
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Requested</th>
                <td className={differences?.route ? 'request-diff' : undefined}>
                  {request.distributorName} · Route {request.routeNumber}
                </td>
                <td>{customer.name}</td>
                <td>{fullAddressOf(customer)}</td>
                <td>{customer.contactName || '—'}</td>
                <td className={differences?.phone ? 'request-diff' : undefined}>
                  {phonesOf(customer)}
                </td>
              </tr>
              <tr>
                <th scope="row">Existing</th>
                <td className={differences?.route ? 'request-diff' : undefined}>
                  {match.distributorName} ·{' '}
                  <a href={routePath(match.distributorId, match.routeNumber)}>
                    Route {match.routeNumber}
                  </a>
                </td>
                <td>{match.stop.name}</td>
                <td>
                  {fullAddressOf({
                    ...match.stop,
                    streetAddress: match.stop.streetAddress ?? '',
                    city: match.stop.city ?? '',
                    state: match.stop.state ?? '',
                    zipCode: match.stop.zipCode ?? '',
                  })}
                </td>
                <td>{match.stop.contactName || '—'}</td>
                <td className={differences?.phone ? 'request-diff' : undefined}>
                  {phonesOf(match.stop)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {request.flaggedAt && (
        <p className="decision-note">
          Flagged with Tropicana by {request.flaggedByName} on {formatDateTime(request.flaggedAt)}.
        </p>
      )}

      {open ? (
        <div className="decision-block">
          <div className="decision-controls list-actions">
            {canFlag(request) && (
              <button type="button" onClick={() => run('flag')} disabled={busy !== null}>
                {busy === 'flag' ? 'Flagging…' : 'Flag with Tropicana'}
              </button>
            )}
            <button
              type="button"
              className="primary"
              onClick={() => run('approve')}
              disabled={busy !== null || blocker !== null}
              title={blocker ?? 'Add this customer to the requested route'}
            >
              {busy === 'approve' ? 'Approving…' : 'Approve'}
            </button>
            {canReject(request) && !rejecting && (
              <button
                type="button"
                className="danger"
                onClick={() => setRejecting(true)}
                disabled={busy !== null}
              >
                Reject
              </button>
            )}
          </div>
          {blocker && request.type === 'conflict' && <p className="decision-note">{blocker}</p>}
          {rejecting && (
            <div className="reject-box">
              <label htmlFor={`reject-feedback-${request.id}`}>
                Feedback for the rep (required)
              </label>
              <textarea
                id={`reject-feedback-${request.id}`}
                value={feedback}
                onChange={(event) => setFeedback(event.target.value)}
                maxLength={1000}
                placeholder="Reason for rejection"
              />
              <div className="decision-controls list-actions">
                <button
                  type="button"
                  className="danger"
                  onClick={() => run('reject')}
                  disabled={busy !== null || feedback.trim() === ''}
                >
                  {busy === 'reject' ? 'Rejecting…' : 'Confirm rejection'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRejecting(false);
                    setFeedback('');
                  }}
                  disabled={busy !== null}
                >
                  Keep open
                </button>
              </div>
            </div>
          )}
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
        </div>
      ) : (
        <div className="decision-summary">
          <strong>{request.decision === 'approved' ? 'Approved' : 'Rejected'}</strong> by{' '}
          {request.decidedByName}
          {request.decidedAt ? ` on ${formatDateTime(request.decidedAt)}` : ''}.
          {request.decision === 'approved' && (
            <>
              {' '}
              Customer added to {request.distributorName}{' '}
              <a href={routePath(request.distributorId, request.routeNumber)}>
                route {request.routeNumber}
              </a>
              .
            </>
          )}
          {request.decision === 'rejected' && (
            <>
              {' '}
              Feedback sent to the rep: <em>{request.feedback}</em>
            </>
          )}
        </div>
      )}
    </div>
  );
}
